import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import OnboardingPortal from './OnboardingPortal';
import OnboardingRequestStatus from './OnboardingRequestStatus';
import { useTranslation } from 'react-i18next';

interface AgentPortalAppProps {
  apiUrl: string;
}

const AgentPortalStatusLookup: React.FC = () => {
  const { t } = useTranslation();
  const [requestId, setRequestId] = React.useState('');
  const navigate = useNavigate();

  const handleLookup = () => {
    const id = requestId.trim();
    if (id) navigate(`/agent-portal/status/${id}`);
  };

  return (
    <div className="page-content" style={{ maxWidth: 600, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 8 }}>{t('pages.agentPortalApp.checkRequestStatus')}</h1>
      <p className="muted-text" style={{ marginBottom: 28 }}>
        {t('pages.agentPortalApp.enterYourRequestIDToView')}
      </p>
      <div className="ent-card">
        <div className="ent-card-head"><h3>{t('pages.agentPortalApp.requestIDLookup')}</h3></div>
        <div className="ent-card-body">
          <div className="ent-field">
            <label>{t('pages.agentPortalApp.requestID')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="ent-input"
                type="text"
                placeholder={t('pages.agentPortalApp.eGReqA1b2c3d4')}
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                style={{ fontFamily: 'var(--mono)', flex: 1 }}
                autoFocus
              />
              <button className="ent-btn primary" onClick={handleLookup} disabled={!requestId.trim()}>
                {t('pages.agentPortalApp.check')}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 0 }}>
            {t('pages.agentPortalApp.yourRequestIDWasShownOn')}
          </p>
        </div>
      </div>
    </div>
  );
};

const AgentPortalApp: React.FC<AgentPortalAppProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isStatus = location.pathname.startsWith('/agent-portal/status');
  const isRequest = location.pathname === '/agent-portal' || location.pathname === '/agent-portal/';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 24px',
        height: 52,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 15, letterSpacing: '-0.3px' }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'var(--accent)', color: '#fff',
            display: 'grid', placeItems: 'center',
            fontSize: 12, fontWeight: 800,
          }}>{t('pages.agentPortalApp.a5')}</div>
          {t('pages.agentPortalApp.agenti5AgentPortal')}
        </div>

        <div style={{ flex: 1 }} />

        <nav style={{ display: 'flex', gap: 4 }}>
          <button
            className={`ent-btn${isRequest ? ' primary' : ' ghost'}`}
            onClick={() => navigate('/agent-portal')}
          >
            {t('pages.agentPortalApp.requestAccess')}
          </button>
          <button
            className={`ent-btn${isStatus ? ' primary' : ' ghost'}`}
            onClick={() => navigate('/agent-portal/status')}
          >
            {t('pages.agentPortalApp.checkStatus')}
          </button>
        </nav>
      </header>

      {/* Content */}
      <main style={{ flex: 1, padding: '32px 24px 64px', maxWidth: 820, width: '100%', margin: '0 auto' }}>
        <Routes>
          <Route path="/" element={<OnboardingPortal apiUrl={apiUrl} portalMode />} />
          <Route path="/status" element={<AgentPortalStatusLookup />} />
          <Route path="/status/:requestId" element={<OnboardingRequestStatus apiUrl={apiUrl} />} />
        </Routes>
      </main>
    </div>
  );
};

export default AgentPortalApp;
